/**
 * AI Agent Node Component
 * Multi-handle node: main input (left arrow), typed outputs (right circles),
 * top inputs (down arrows), bottom outputs (down circles).
 */

import React from 'react';
import { NodeProps, Handle, Position } from 'reactflow';
import { WorkflowNode, getNodeDisplayProps, ExecutionStatus } from '@flow-studio/types';
import { BaseNodeClass, withNodeHooks, NodeConfig } from './Base/BaseNode';
import { NodeSearchModal } from '../Modals/NodeSearchModal';
import { BotIcon, UsersIcon, BrainIcon, DatabaseIcon, WrenchIcon } from '@bizfirst/common-react';

const PinIcon = ({ size = 11, color = 'white' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);

// ─── Shared shape helpers ────────────────────────────────────────────────────

// Input arrow: pentagon pointing right (tip touches node border, base outside)
const inputArrowLeft: React.CSSProperties = {
  width: '14px', height: '10px',
  background: 'rgba(255,255,255,0.75)',
  border: 'none', borderRadius: 0,
  clipPath: 'polygon(0 0, 65% 0, 100% 50%, 65% 100%, 0 100%)',
  transform: 'translate(-100%, -50%)',
  cursor: 'crosshair',
  boxShadow: 'none',
};

// Down-pointing chevron for top input handles (tip points into node from above)
const inputArrowTop: React.CSSProperties = {
  width: '10px', height: '14px',
  background: 'rgba(255,255,255,0.75)',
  border: 'none', borderRadius: 0,
  clipPath: 'polygon(0 100%, 0 35%, 50% 0%, 100% 35%, 100% 100%)',
  transform: 'translate(-50%, -100%)',
  cursor: 'crosshair',
  boxShadow: 'none',
};

type HandleShape = 'circle' | 'diamond' | 'square' | 'arrow';

const outputHandle = (color: string, pos: 'right' | 'bottom' | 'left' | 'top', shape: HandleShape = 'circle'): React.CSSProperties => {
  let baseTransform: string;
  switch (pos) {
    case 'right':  baseTransform = 'translate(calc(50% + 3px), -50%)'; break;
    case 'bottom': baseTransform = 'translate(-50%, calc(50% + 3px))'; break;
    case 'left':   baseTransform = 'translate(calc(-50% - 6px), -50%)'; break;
    case 'top':    baseTransform = 'translate(-50%, calc(-50% - 3px))'; break;
  }
  const base: React.CSSProperties = {
    width: '10px', height: '10px',
    background: color,
    border: '1.5px solid rgba(255,255,255,0.25)',
    cursor: 'crosshair',
  };
  switch (shape) {
    case 'arrow':
      return {
        ...base,
        width: '14px', height: '10px',
        borderRadius: 0, border: 'none',
        clipPath: 'polygon(0 0, 65% 0, 100% 50%, 65% 100%, 0 100%)',
        transform: pos === 'right' ? 'translate(calc(50% - 1px), -50%)' : baseTransform,
      };
    case 'diamond':
      return { ...base, borderRadius: '2px', transform: `${baseTransform} rotate(45deg)` };
    case 'square':
      return { ...base, borderRadius: '2px', transform: baseTransform };
    default:
      return { ...base, borderRadius: '50%', transform: baseTransform };
  }
};

// ─── AIAgentNodeClass ─────────────────────────────────────────────────────────

class AIAgentNodeClass extends BaseNodeClass {
  // Do NOT redeclare state — BaseNodeState already has showNodeSearchModal
  // and selectedSourceHandle as optional fields. Overriding state here would
  // clobber isCollapsed and other BaseNodeState fields.

  protected config(): NodeConfig {
    return {
      ...super.config(),
      showToolbarOnClick: true,
      showToolbarOnHover: false,
      renderHandlesManually: true,
    };
  }

  protected getClassName(): string {
    const { selected, data } = this.props;
    return `ai-agent-node ${selected ? 'selected' : ''} ${data.processElement?.isDisabled ? 'disabled' : ''}`;
  }

  protected getDefaultNodeWidth(): number  { return 350; }
  protected getDefaultNodeHeight(): number { return 220; }

  protected getContainerStyle(): React.CSSProperties {
    const { selected, data } = this.props;
    const isCollapsed = this.state.isCollapsed;

    return {
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      border: `2px solid ${selected ? '#309B48' : 'rgba(99,102,241,0.3)'}`,
      borderRadius: '12px',
      minWidth: isCollapsed ? '200px' : '350px',
      minHeight: isCollapsed ? '40px' : '180px',
      maxHeight: isCollapsed ? '50px' : undefined,
      opacity: data.processElement?.isDisabled ? 0.6 : 1,
      boxShadow: selected
        ? '0 0 0 3px rgba(48,155,72,0.25), 0 4px 12px rgba(0,0,0,0.35)'
        : '0 4px 12px rgba(0,0,0,0.35)',
      transition: 'all 0.2s',
      cursor: 'default',
      position: 'relative',
      paddingTop:    '0',
      paddingBottom: '0',
    };
  }

  protected renderContent(): React.ReactNode {
    const { data } = this.props;
    const display = getNodeDisplayProps(data);

    return (
      <>
        <div className="bf-node-header" style={this.headerStyle()}>
          <BotIcon size={20} />
          <span
            className="bf-node-header-title"
            title={display.displayName || 'AI Agent'}
            style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {display.displayName || 'AI Agent'}
          </span>
          {data.processElement?.pinnedData && (
            <span title="Output pinned" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, opacity: 0.85 }}>
              <PinIcon size={11} color="white" />
            </span>
          )}
        </div>

        {/* AI badge — floated out of header, side by side with satellite badge */}
        <span style={this.badgeStyle()}>AI</span>

        <div className="bf-node-body nodrag" style={{ padding: '16px', color: '#fff', overflow: 'hidden' }}>
          {display.description && (
            <div style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)', marginTop: '4px', wordBreak: 'break-word' }}>
              {display.description}
            </div>
          )}
          {display.executionStatus && display.executionStatus !== 'idle' && !['success', 'failed', 'skipped', 'timeout'].includes(display.executionStatus) && (
            <div style={this.statusStyle(display.executionStatus)}>
              {display.executionStatus === 'running'  && '⟳ '}
              {display.executionStatus === 'error'    && '✗ '}
              {display.executionStatus}
            </div>
          )}
        </div>
      </>
    );
  }

  // ── Left input handle ───────────────────────────────────────────────────────

  protected renderInputHandles(): React.ReactNode {
    const { id } = this.props;
    const edges = this.props.workflowStore.edges;
    const uiAny = this.props.uiStore as any;
    const hideUnassigned = uiAny.hideUnassignedPorts === true ||
      (uiAny.hiddenPortsNodeIds ?? []).includes(id);
    const connectedIn = (handleId: string) =>
      edges.some((e: any) => e.target === id && e.targetHandle === handleId);

    if (hideUnassigned && !connectedIn('main')) return null;

    return (
      <>
        <span style={{
          ...this.innerLabelStyle(),
          position: 'absolute',
          left: '0',
          top: '50%',
          transform: 'translate(calc(-100% - 19px), -50%)',
          pointerEvents: 'none',
        }}>Input</span>
        <Handle
          type="target"
          position={Position.Left}
          id="main"
          style={inputArrowLeft}
          title="Main Input"
          isConnectable
          onClick={e => {
            e.stopPropagation();
            this.props.uiStore.setSelectedHandle({ nodeId: id, handleId: 'main', handleType: 'target' });
          }}
        />
      </>
    );
  }

  // ── Right + top + bottom + left output / input handles ──────────────────────

  protected renderOutputHandles(): React.ReactNode {
    const { id } = this.props;
    const edges = this.props.workflowStore.edges;
    const isCollapsed = this.state.isCollapsed;
    const hideSatellites = this.props.uiStore.hideSatellites;
    const uiAny = this.props.uiStore as any;
    const hideUnassigned = uiAny.hideUnassignedPorts === true ||
      (uiAny.hiddenPortsNodeIds ?? []).includes(id);

    const connectedOut = (handleId: string) =>
      edges.some((e: any) => e.source === id && e.sourceHandle === handleId);
    const connectedIn = (handleId: string) =>
      edges.some((e: any) => e.target === id && e.targetHandle === handleId);

    const showPort = (handleId: string, isOutput: boolean) =>
      !hideUnassigned || (isOutput ? connectedOut(handleId) : connectedIn(handleId));

    return (
      <>
        <style>{`
          .ai-agent-add-btn { opacity: 0; transition: opacity 0.15s; }
          .ai-agent-handle-wrapper:hover .ai-agent-add-btn { opacity: 1; }
        `}</style>

        {/* ── Right: Output only ──────────────────────────────── */}
        {showPort('output', true) && (
          <>
            <div style={this.rightContainerStyle()}>
              {this.renderRightHandle('output', '#10b981', null, null, !connectedOut('output'), 'arrow')}
            </div>
            <span style={{
              ...this.innerLabelStyle(),
              position: 'absolute',
              right: '0',
              top: '50%',
              transform: 'translate(calc(100% + 18px), -50%)',
              pointerEvents: 'none',
            }}>Output</span>
          </>
        )}

        {/* ── Left: Extends (source, diamond) near top ──────────── */}
        {!isCollapsed && !hideSatellites && showPort('extends', true) && (
          this.renderLeftSourceHandle('extends', '#14b8a6', '⬆ Extends', true, 'diamond')
        )}

        {/* ── Top: LLM (target), Memory (target) ────────────────────── */}
        {!isCollapsed && !hideSatellites && (showPort('llm', false) || showPort('memory', false)) && (
          <div style={this.topContainerStyle()}>
            {showPort('llm',    false) && this.renderTopHandle('llm',    '#ef4444', <><BrainIcon size={12}/> LLM</>,    true)}
            {showPort('memory', false) && this.renderTopHandle('memory', '#8b5cf6', <><DatabaseIcon size={12}/> Memory</>, true, -85, -10)}
          </div>
        )}

        {/* ── Left: Sub Agent (target) above Extends (source) ─────── */}
        {!isCollapsed && !hideSatellites && showPort('team-member', false) && (
          this.renderLeftTargetHandle('team-member', '#ec4899', <><UsersIcon size={12}/> Sub Agent</>, 12)
        )}

        {/* ── Bottom: Tools (target), Base Agent (target), Sub Agents (source) ─ */}
        {!isCollapsed && !hideSatellites && (showPort('tool', false) || showPort('base-node', false) || showPort('team-leader', true)) && (
          <div style={this.bottomContainerStyle()}>
            {showPort('tool',        false) && this.renderBottomTargetHandle('tool',      '#f59e0b', <><WrenchIcon size={12}/> Tools</>, true)}
            {showPort('base-node',   false) && this.renderBottomTargetHandle('base-node', '#06b6d4', '↓ Base Agent')}
            {showPort('team-leader', true)  && this.renderBottomHandle('team-leader', '#f97316', <><UsersIcon size={12}/> Sub Agents</>, true, 'square')}
          </div>
        )}

        {/* ── Node search modal ──────────────────────────────── */}
        {this.state.showNodeSearchModal && this.state.selectedSourceHandle && (
          <NodeSearchModal
            sourceNodeId={id}
            sourceHandleId={this.state.selectedSourceHandle}
            onClose={() => this.setState({ showNodeSearchModal: false, selectedSourceHandle: null })}
          />
        )}
      </>
    );
  }

  // ── Handle builders ─────────────────────────────────────────────────────────

  private renderRightHandle(
    handleId: string,
    color: string,
    label: React.ReactNode,
    _unused: null,
    alwaysShowAdd: boolean,
    shape: HandleShape = 'circle',
  ): React.ReactNode {
    return (
      <div className="ai-agent-handle-wrapper" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px', position: 'relative' }}>
        {label && <span style={this.innerLabelStyle()}>{label}</span>}
        <Handle
          type="source"
          position={Position.Right}
          id={handleId}
          style={outputHandle(color, 'right', shape)}
          title={handleId}
          isConnectable
        />
        {alwaysShowAdd && this.renderAddBtn(handleId, {
          position: 'absolute', right: '-32px', top: 'calc(50% - 18px)', transform: 'translateY(-50%)',
        })}
      </div>
    );
  }

  private renderTopHandle(handleId: string, color: string, label: React.ReactNode, alwaysShowAdd = false, leftShift = 0, labelShift = 0): React.ReactNode {
    return (
      <div className="ai-agent-handle-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, position: 'relative', transform: leftShift !== 0 ? `translateX(${leftShift}px)` : undefined }}>
        <span style={{ ...this.innerLabelStyle(), marginBottom: '4px', transform: labelShift !== 0 ? `translateX(${labelShift}px)` : undefined }}>{label}</span>
        <Handle
          type="target"
          position={Position.Top}
          id={handleId}
          style={{ ...inputArrowTop, background: color }}
          title={handleId}
          isConnectable
        />
        {alwaysShowAdd && this.renderAddBtn(handleId, {
          position: 'absolute', top: '-32px', left: '50%', transform: 'translateX(-50%)',
        })}
      </div>
    );
  }

  private renderTopSourceHandle(
    handleId: string,
    color: string,
    label: React.ReactNode,
    alwaysShowAdd: boolean,
    shape: HandleShape = 'circle',
  ): React.ReactNode {
    return (
      <div className="ai-agent-handle-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, position: 'relative' }}>
        <Handle
          type="source"
          position={Position.Top}
          id={handleId}
          style={outputHandle(color, 'top', shape)}
          title={handleId}
          isConnectable
        />
        <span style={{ ...this.innerLabelStyle(), marginTop: '6px' }}>{label}</span>
        {alwaysShowAdd && this.renderAddBtn(handleId, {
          position: 'absolute', top: '-32px', left: '50%', transform: 'translateX(-50%)',
        })}
      </div>
    );
  }

  private renderLeftSourceHandle(
    handleId: string,
    color: string,
    label: React.ReactNode,
    alwaysShowAdd: boolean,
    shape: HandleShape = 'circle',
  ): React.ReactNode {
    return (
      <>
        {/* Label outside the node, to the left of the diamond */}
        <span style={{
          ...this.innerLabelStyle(),
          position: 'absolute',
          left: '0',
          top: 'calc(25% + 5px)',
          transform: 'translate(calc(-100% - 20px), -50%)',
          pointerEvents: 'none',
        }}>{label}</span>

        <div
          className="ai-agent-handle-wrapper"
          style={{ position: 'absolute', left: '0', top: 'calc(25% + 5px)', transform: 'translateY(-50%)' }}
        >
          <Handle
            type="source"
            position={Position.Left}
            id={handleId}
            style={outputHandle(color, 'left', shape)}
            title={handleId}
            isConnectable
          />
          {alwaysShowAdd && this.renderAddBtn(handleId, {
            position: 'absolute', left: '-36px', top: '50%', transform: 'translateY(-50%)',
          })}
        </div>
      </>
    );
  }

  private renderLeftTargetHandle(
    handleId: string,
    color: string,
    label: React.ReactNode,
    topPercent: number,
  ): React.ReactNode {
    return (
      <>
        <span style={{
          ...this.innerLabelStyle(),
          position: 'absolute',
          left: '0',
          top: `${topPercent}%`,
          transform: 'translate(calc(-100% - 23px), -50%)',
          pointerEvents: 'none',
        }}>{label}</span>
        <div style={{ position: 'absolute', left: '0', top: `${topPercent}%` }}>
          <Handle
            type="target"
            position={Position.Left}
            id={handleId}
            style={{ ...inputArrowLeft, background: color }}
            title={handleId}
            isConnectable
          />
        </div>
      </>
    );
  }

  private renderBottomHandle(
    handleId: string,
    color: string,
    label: React.ReactNode,
    alwaysShowAdd: boolean,
    shape: HandleShape = 'circle',
  ): React.ReactNode {
    return (
      <div className="ai-agent-handle-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, position: 'relative' }}>
        <span style={this.innerLabelStyle()}>{label}</span>
        <Handle
          type="source"
          position={Position.Bottom}
          id={handleId}
          style={outputHandle(color, 'bottom', shape)}
          title={handleId}
          isConnectable
        />
        {alwaysShowAdd && this.renderAddBtn(handleId, {
          position: 'absolute', top: '28px', left: '50%', transform: 'translateX(-50%)',
        })}
      </div>
    );
  }

  private renderBottomTargetHandle(
    handleId: string,
    color: string,
    label: React.ReactNode,
    alwaysShowAdd = false,
  ): React.ReactNode {
    return (
      <div className="ai-agent-handle-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, position: 'relative' }}>
        <span style={this.innerLabelStyle()}>{label}</span>
        <Handle
          type="target"
          position={Position.Bottom}
          id={handleId}
          style={{ ...inputArrowTop, background: color, transform: 'translate(-50%, 100%) rotate(180deg)' }}
          title={handleId}
          isConnectable
        />
        {alwaysShowAdd && this.renderAddBtn(handleId, {
          position: 'absolute', top: '28px', left: '50%', transform: 'translateX(-50%)',
        })}
      </div>
    );
  }

  private renderAddBtn(handleId: string, posStyle: React.CSSProperties): React.ReactNode {
    return (
      <button
        className="nodrag nopan ai-agent-add-btn"
        style={{
          width: '18px', height: '18px', borderRadius: '50%',
          background: '#309B48', border: '1.5px solid rgba(255,255,255,0.3)',
          color: '#fff', fontSize: '13px', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 10,
          pointerEvents: 'auto',
          ...posStyle,
        }}
        onClick={e => {
          e.stopPropagation();
          this.setState({ showNodeSearchModal: true, selectedSourceHandle: handleId });
        }}
        onMouseDown={e => e.stopPropagation()}
        title="Add node"
      >
        +
      </button>
    );
  }

  // ── Container style helpers ─────────────────────────────────────────────────

  private rightContainerStyle(): React.CSSProperties {
    return {
      position: 'absolute', right: '-8px', top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'flex-end',
    };
  }

  private topContainerStyle(): React.CSSProperties {
    return {
      position: 'absolute', top: '-30px', left: 0, right: 0,
      display: 'flex', justifyContent: 'space-around', gap: '16px',
      paddingBottom: '8px', paddingLeft: '8px', paddingRight: '8px',
    };
  }

  private bottomContainerStyle(): React.CSSProperties {
    return {
      position: 'absolute', bottom: '-30px', left: 0, right: 0,
      display: 'flex', justifyContent: 'space-around', gap: '8px',
      paddingTop: '8px', paddingLeft: '8px', paddingRight: '8px',
    };
  }

  // ── Visual style helpers ────────────────────────────────────────────────────

  private headerStyle(): React.CSSProperties {
    return {
      background: 'rgba(255,255,255,0.15)',
      backdropFilter: 'blur(10px)',
      padding: '12px 16px',
      borderTopLeftRadius: '8px', borderTopRightRadius: '8px',
      display: 'flex', alignItems: 'center', gap: '10px',
      fontSize: '15px', fontWeight: 700, color: '#fff', cursor: 'move',
    };
  }

  private badgeStyle(): React.CSSProperties {
    return {
      position: 'absolute',
      top: '4px',
      right: '48px',
      padding: '2px 6px',
      background: 'rgba(255,255,255,0.2)', borderRadius: '4px',
      fontSize: '9px', fontWeight: 600,
      zIndex: 26,
      pointerEvents: 'none',
    };
  }

  private innerLabelStyle(): React.CSSProperties {
    return {
      fontSize: '10px', fontWeight: 600, color: '#fff',
      pointerEvents: 'none', textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
      background: 'rgba(0,0,0,0.35)', padding: '2px 6px', borderRadius: '4px',
      whiteSpace: 'nowrap',
    };
  }

  private statusStyle(status: string): React.CSSProperties {
    const map: Record<string, string> = {
      success: '#10b981', error: '#ef4444', running: '#3b82f6',
    };
    const c = map[status] || 'transparent';
    return {
      marginTop: '8px', padding: '6px 10px', borderRadius: '6px',
      fontSize: '11px', fontWeight: 600,
      background: `${c}33`, color: c, border: `1px solid ${c}`,
    };
  }
}

export const AIAgentNode: React.FC<NodeProps<WorkflowNode['data']>> =
  withNodeHooks(AIAgentNodeClass);
